import React from 'react'
import { Skeleton, Typography, Button, Row, Col, Form, Input, Transfer, Divider, message } from 'antd'
import { FormComponent, BaseFormComponentProps } from '../../BaseComponent'
import IAdminUser from '../../../interfaces/IAdminUser'
import IAdminRole from '../../../interfaces/IAdminRole'
import { SearchRequest, AdminRoleListRequest, AdminUserCreateRequest, AdminUserUpdateRequest }from '../../../services/api/interfaces/ApiRequest'

type AdminUserProps = BaseFormComponentProps & {
  uuid?: string,
  data?: IAdminUser,
  roles: IAdminRole[],
  error: any | null,

  loading: boolean,
  contentLoading: boolean,
  history: any,

  loadAdminUser: (args: SearchRequest) => void,
  loadAdminRoles: (args: AdminRoleListRequest) => void,

  dispatchCreate: (args: AdminUserCreateRequest) => void,
  dispatchUpdate: (args: AdminUserUpdateRequest) => void
}

export default class AdminUser extends FormComponent<AdminUserProps> {

  state = {
    saving: false,
    loaded: false,
    rolesSelected: [] as string[]
  }

  componentDidMount() {
    const { contentLoading, loadAdminRoles } = this.props

    // Check permissions
    this.validateAccessPermissions()

    // Load roles
    if (!contentLoading) {
      loadAdminRoles({
        role: 1
      })
    }
  }

  componentDidUpdate(prevProps: AdminUserProps, prevState) {
    const wasLoading = prevProps.loading && !this.props.loading
    let newState = Object.assign({}, this.state)

    // Check permissions
    this.validateAccessPermissions()

    // We are loaded?
    if (!this.state.loaded) {
      // We have loaded our contents?
      if (!this.props.contentLoading && this.props.roles) {
        // Let's load our entity
        if (!this.props.loading && !this.props.data && this.props.uuid) {
          this.props.loadAdminUser({
            search: {
              uuid: this.props.uuid,
              roles: {
                with: true
              }
            }
          })

          return
        }

        // If our entity was loaded or if we are creating a new one, that's means that we are loaded :P
        if (wasLoading || !this.props.uuid) {
          newState.loaded = true

          if (this.props.data) {
            const { roles } = this.props.data
            newState.rolesSelected = roles?.map(role => role.item_name) || []
          }
        }
      }
    } else {
      // We are saving
      if (this.state.saving && wasLoading) {
        // Add the new role name to url
        if (!this.props.uuid && this.props.data) {
          this.props.history.replace(`/admin/user/${this.props.data.uuid}`)
        }

        if (!this.props.error) {
          message.success('User saved successfully!')
        } else {
          message.error('Failed to save user. Error: ' + this.props.error)
        }

        newState.saving = false
      }
    }

    // Update if changed
    if (newState.loaded !== this.state.loaded) {
      this.setState(newState)
    }
  }

  handleRoleSelection = (targetKeys: any, direction: any, moveKeys: any) => {
    this.setState({
      rolesSelected: targetKeys
    })
  }

  handleSave = (values) => {
    const { uuid, data, dispatchCreate, dispatchUpdate } = this.props
    const { rolesSelected } = this.state

    if (!uuid) {
      const payload = {
        ...values,
        roles: rolesSelected
      }

      dispatchCreate({
        admin_user: payload
      })
      this.setState({
        saving: true
      })
    } else {
      const removed = data?.roles?.map(p => p.item_name).filter(role => !rolesSelected.includes(role)) || []

      const payload = {
        ...values,
        uuid,
        roles: rolesSelected,
        roles_deleted: removed
      }

      dispatchUpdate({
        admin_user: payload
      })
      this.setState({
        saving: true
      })
    }
  }

  render() {
    const { uuid, data, roles, loading } = this.props
    const { loaded, rolesSelected } = this.state

    if (!loaded) {
      return <Skeleton />
    }

    const rolesOptions = roles?.map(role => {
      return {
        key: role.name,
        title: role.rule_name || role.name,
        description: role.description,
        chosen: rolesSelected.includes(role.name)
      }
    })

    let values: any = {}
    if (uuid) {
      values = {
        ...data || {}
      }
    }

    return (
      <Form
        name="admin_user"
        initialValues={values}
        layout="vertical"
        onFinish={this.handleSave}
      >
        <Row gutter={12}>
          <Col xs={24} sm={12} md={12}>
            <Form.Item
              label="First Name"
              name="first_name"
              rules={[
                {
                  required: true,
                  message: 'Missing a first name'
                }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={12}>
            <Form.Item
              label="Last Name"
              name="last_name"
              rules={[
                {
                  required: true,
                  message: 'Missing a last name'
                }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        {
          !uuid ? (
            <Row gutter={12}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    {
                      type: 'email',
                      message: 'This is not a valid email'
                    },
                    {
                      required: true,
                      message: 'Missing an email'
                    }
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          ) : null
        }

        <Divider />

        <Row>
          <Col xs={24}>
            <Typography.Title level={5}>Roles</Typography.Title>
            <Transfer
              className="shelter-transfer"
              dataSource={rolesOptions}
              targetKeys={rolesSelected}
              render={(item: any) => item.title}
              onChange={this.handleRoleSelection}
            />
          </Col>
        </Row>
        
        <Form.Item style={{marginTop: 32}}>
          <Button type="primary" htmlType="submit" size="large" loading={loading} disabled={rolesSelected.length === 0} ref={(refs) => this.actionRefs.save = refs} block>
            Save
          </Button>
        </Form.Item>
      </Form>
    )
  }
}
